import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { LIST, COMPONENT, DASH_VIEW } from 'utils/constants'
import USER_SETTINGS from 'constants/userSettings'
import { DEFAULT_DRAWER_WIDTH } from 'utils/drawerConstants'
import { useSelector, useMakeSelector } from 'hooks/redux'
import {
  DASH_TYPE_Q_BUS_DATABOOK_CODE,
  HARDWIRED_SWITCH,
  SWITCH,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import { prop } from 'ramda'
import {
  createToolTipContentComponent,
  isPanelPartSelected,
  getComponentImageFile,
} from 'components/SwitchAndGauge/utils'
import { getDependencyDimensions } from 'components/SwitchAndGauge/dependencyDimensions'

import { getUserSetting } from 'selectors/user'
import {
  makeGetPanelPart,
  getHasComponentMoved,
  getShouldHidePendingPill,
  getLocationDetail,
  makeGetLocationWidthAndHeight,
  getDashboardType,
  makeGetIsBeingDragged,
  getDataBookCodes,
} from 'selectors/dash'
import ChecTooltip from 'components/chec/ChecTooltip'
import { getDrawerWidth } from 'selectors/drawer'
import { useDispatch } from 'react-redux'
import { setDrawerVisibleWidth, setDrawerWidth } from 'actions/drawer'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import { makeComponentMetaSelector } from 'selectors/navigator'
import DraggableRef from './DraggableRef'
import DroppableRef from './DroppableRef'
import { PartGraphicCustom } from './PartGraphicCustom'
import PartGraphic from './PartGraphic'
import {
  Pill,
  TooltipPositioner,
  PartPositioner,
  RemovedItemBorder,
  StyledPart,
} from './Part.style'
import CustomGraphicIcon from './CustomGraphicIcon'
import HardwiredSwitchIcon from './HardwiredSwitchIcon'

export function PanelPart({
  className,
  onClick,
  location,
  isCustomSwitch,
  isCustomIndicator,
  isCustomHardWiredSwitch,
  capPartNumber,
  selected,
  hasChanged,
  dimensions,
  Part,
  detail,
  graphicNotFound,
  dashboardType,
  panelNumber,
  panel,
  vehicleSubType,
  imageUrls,
  preferLocationShape,
}) {
  return (
    <GraphicWrapper
      className={`graphic ${className}`}
      onClick={onClick}
      location={location}
    >
      {(isCustomSwitch || isCustomIndicator || isCustomHardWiredSwitch) &&
      capPartNumber ? (
        <PartGraphicCustom
          className={`custom-graphic ${className}`}
          location={location}
          capPartNumber={capPartNumber}
          selected={selected}
          hasChanged={hasChanged}
          isCustomIndicator={isCustomIndicator}
          dimensions={dimensions}
          panelNumber={panelNumber}
          vehicleSubType={vehicleSubType}
        />
      ) : (
        <PartGraphic
          className={`part-graphic ${className}`}
          location={location}
          Part={Part}
          graphicNotFound={graphicNotFound}
          selected={selected}
          hasChanged={hasChanged}
          detail={detail}
          dashboardType={dashboardType}
          dimensions={dimensions}
          panelNumber={panelNumber}
          panel={panel}
          imageUrls={imageUrls}
          preferLocationShape={preferLocationShape}
        />
      )}
    </GraphicWrapper>
  )
}

PanelPart.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  location: PropTypes.string,
  isCustomSwitch: PropTypes.bool,
  isCustomIndicator: PropTypes.bool,
  isCustomHardWiredSwitch: PropTypes.bool,
  capPartNumber: PropTypes.string,
  selected: PropTypes.bool,
  hasChanged: PropTypes.bool,
  dimensions: PropTypes.object,
  Part: PropTypes.elementType,
  detail: PropTypes.string,
  graphicNotFound: PropTypes.bool,
  dashboardType: PropTypes.string,
  panelNumber: PropTypes.string,
  panel: PropTypes.string,
  vehicleSubType: PropTypes.string,
  imageUrls: PropTypes.arrayOf(PropTypes.string),
  preferLocationShape: PropTypes.bool,
}

// PBI 479416: use the edited location dimensions when drawing dependency frames.
const Part = React.memo(
  ({
    noTooltip,
    disableEvents,
    location = '',
    dependency,
    openListView,
    panel,
    panelNumber: propPanelNumber, // this is for add component to work
    top,
    left,
    zIndex,
    width,
    height,
    detailView,
    isPanel,
    hidePendingPill: propHidePendingPill,
  }) => {
    const componentMeta = useMakeSelector(makeComponentMetaSelector, {
      id: DASH_VIEW,
    })

    const drawerWidth = useSelector(getDrawerWidth)
    const dispatch = useDispatch()

    const {
      Part: PartSvg,
      meta,
      graphicNotFound,
      preferLocationShape,
    } = useMakeSelector(makeGetPanelPart, {
      location,
      panelNumber: propPanelNumber,
      isPanel,
    })
    const showPartNumbers = useSelector(
      getUserSetting,
      USER_SETTINGS.SWITCH_AND_GAUGE_SHOW_PART_NUMBERS
    )
    const hasMoved = useMakeSelector(getHasComponentMoved, location)
    const selectorHidePendingPill = useMakeSelector(
      getShouldHidePendingPill,
      location
    )
    const hidePendingPill = propHidePendingPill || selectorHidePendingPill
    const detail = useSelector(getLocationDetail, location)
    const dashboardType = useSelector(getDashboardType)
    const dataBookCodes = useSelector(getDataBookCodes)

    const {
      panelDescription,
      capPartNumber,
      capDescription,
      dashObjectName,
      dashObjectDescription,
      isSwitchRemoved,
      panelNumber = '',
      isCustom: isCustomSwitch,
      isCustomIndicator,
      type = '',
      imageUrl: imageUrls,
    } = meta

    const defaultDrawerWidth = drawerWidth === DEFAULT_DRAWER_WIDTH

    const isHardwiredSwitch = type === HARDWIRED_SWITCH

    const isCustomHardWiredSwitch = isHardwiredSwitch && capPartNumber

    const handleOnClick = () => {
      if (disableEvents) return

      if (!openListView && !defaultDrawerWidth) {
        dispatch(setDrawerWidth(DEFAULT_DRAWER_WIDTH))
        dispatch(setDrawerVisibleWidth(DEFAULT_DRAWER_WIDTH))
      }

      // ToDo: This should just take the location and check if location has children.
      const view = openListView ? LIST : COMPONENT
      const id = openListView ? panel : location

      dispatch(
        setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
          view,
          id,
        })
      )
    }

    const classes = `location-${location} panel-number-${panelNumber.replaceAll(
      ' ',
      '-'
    )} detail-${detail}`
    const position = { top, left, zIndex }
    const PartDependency = getComponentImageFile(
      prop('panelNumber', dependency),
      true
    )
    const selectedPanelLocation = prop('id', componentMeta)
    const selected = isPanelPartSelected(selectedPanelLocation, location, panel)
    const showPendingPill = !hidePendingPill && !detailView && hasMoved
    const isBeingDragged = useMakeSelector(makeGetIsBeingDragged, location)
    const [tooltipVisible, setTooltipVisible] = useState(false)

    useEffect(() => {
      if (isBeingDragged) {
        setTooltipVisible(false)
      }
    }, [isBeingDragged])

    const isTypeSwitch = type.includes(SWITCH.toLowerCase())
    const isCustom = isCustomSwitch || isCustomIndicator

    const toolTipTitle =
      !noTooltip &&
      !isBeingDragged &&
      createToolTipContentComponent(
        panelNumber,
        location,
        panelDescription,
        showPartNumbers,
        capDescription,
        capPartNumber,
        dashObjectName,
        dashObjectDescription,
        isCustomSwitch,
        isHardwiredSwitch,
        isTypeSwitch,
        isCustomIndicator,
        isCustomHardWiredSwitch
      )

    if (isSwitchRemoved)
      return (
        <PartPositioner
          className={`removed-part ${classes}`}
          position={position}
        >
          <RemovedItemBorder removed={isSwitchRemoved} />
          <Pill removed={isSwitchRemoved}>{strings.removed}</Pill>
        </PartPositioner>
      )

    const isDashTypeQBus =
      ['Q'].includes(dashboardType) &&
      dataBookCodes.includes(DASH_TYPE_Q_BUS_DATABOOK_CODE)

    const hideDependency =
      (isDashTypeQBus && ['AUPNL'].includes(panel)) || !dependency

    // PBI 479416: use the same inset-based frame sizing as the details drawer.
    const dependencyDimensions = getDependencyDimensions(dependency, {
      width,
      height,
    })

    const dependencyPart = !hideDependency ? (
      <PartPositioner
        className={`dependency-graphix ${classes}`}
        position={{
          top: top - prop('top', dependency),
          left: left - prop('left', dependency),
          zIndex: prop('zIndex', dependency),
        }}
        dimensions={dependencyDimensions}
      >
        <PartDependency disableEvents style={dependencyDimensions} />
      </PartPositioner>
    ) : null

    if (!PartSvg) {
      return null
    }

    const renderPanelPart = (
      <PanelPart
        className={classes}
        onClick={handleOnClick}
        location={location}
        isCustomSwitch={isCustomSwitch}
        isCustomIndicator={isCustomIndicator}
        isCustomHardWiredSwitch={isCustomHardWiredSwitch}
        capPartNumber={capPartNumber}
        selected={selected}
        hasChanged={showPendingPill}
        dimensions={{ width, height }}
        Part={PartSvg}
        detail={detail}
        graphicNotFound={graphicNotFound}
        dashboardType={dashboardType}
        panelNumber={panelNumber}
        panel={panel}
        imageUrls={imageUrls}
        preferLocationShape={preferLocationShape}
      />
    )

    if (disableEvents) {
      return (
        <PartPositioner
          className={`disabled-events ${classes}`}
          position={position}
          detailView={detailView}
        >
          {renderPanelPart}
          {showPendingPill && <Pill>{strings.pending.toUpperCase()}</Pill>}
        </PartPositioner>
      )
    }

    return (
      <>
        {dependencyPart}
        <PartPositioner
          className={`part-positioner ${classes}`}
          position={position}
          detailView={detailView}
          isBeingDragged={isBeingDragged}
        >
          <ChecTooltip
            title={toolTipTitle}
            visible={tooltipVisible && !!toolTipTitle}
            onVisibleChange={setTooltipVisible}
            overlayStyle={isCustomHardWiredSwitch ? { maxWidth: 'none' } : {}}
          >
            <TooltipPositioner showPartNumbers={showPartNumbers}>
              <DroppableRef location={location}>
                <DraggableRef location={location}>
                  {renderPanelPart}
                  {isCustomHardWiredSwitch ? (
                    <>
                      <CustomGraphicIcon />
                      <HardwiredSwitchIcon />
                    </>
                  ) : (
                    <>
                      {isCustom && <CustomGraphicIcon />}
                      {isHardwiredSwitch && <HardwiredSwitchIcon />}
                    </>
                  )}
                </DraggableRef>
              </DroppableRef>
            </TooltipPositioner>
          </ChecTooltip>
          {showPendingPill && <Pill>{strings.pending.toUpperCase()}</Pill>}
        </PartPositioner>
      </>
    )
  }
)

Part.defaultProps = {
  panelNumber: '',
}

Part.propTypes = {
  className: PropTypes.string,
  panelNumber: PropTypes.string,
  panelDescription: PropTypes.string,
  location: PropTypes.string,
  noTooltip: PropTypes.bool,
  disableEvents: PropTypes.bool,
  panelLocation: PropTypes.string,
  dependency: PropTypes.object,
  openListView: PropTypes.bool,
  noShowPartNumber: PropTypes.bool,
  panel: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  top: PropTypes.number,
  left: PropTypes.number,
  zIndex: PropTypes.number,
  detailView: PropTypes.bool,
  isPanel: PropTypes.bool,
  hidePendingPill: PropTypes.bool,
}

export default Part

const GraphicWrapper = ({ location, onClick, children }) => {
  const dimensions = useMakeSelector(makeGetLocationWidthAndHeight, location)

  return (
    <StyledPart dimensions={dimensions} onClick={onClick}>
      {children}
    </StyledPart>
  )
}

GraphicWrapper.propTypes = {
  location: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
